import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  Input,
  OnInit,
  SecurityContext,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  heroHandThumbDown,
  heroHandThumbUp,
} from '@ng-icons/heroicons/outline';
import {
  heroHandThumbDownSolid,
  heroHandThumbUpSolid,
  heroPencilSquareSolid,
  heroUserSolid,
} from '@ng-icons/heroicons/solid';
import { MarkdownComponent, provideMarkdown } from 'ngx-markdown';
import { lastValueFrom } from 'rxjs';
import { signal, effect } from '@angular/core';

// Add Clarity imports
import { ClarityModule } from '@clr/angular';

import { MenuModule } from 'primeng/menu';
import { AiService } from '../../services/ai/ai.service';
import { OauthService, UserInfo } from '../../services/oauth.service';
import { RepoService } from '../../services/repo.service';
import { DbService } from '../../services/db.service';
import { UserSettingsService } from '../../services/user-settings.service';
import { ShellComponent } from '../shell/shell.component';
import { WorkflowService, WorkflowType } from '../../services/workflow.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { TextareaComponent } from '../../components/textarea/textarea.component';
// import { components } from '../../schema/backend';

export type Message = {
  session_id: string;
  message_id: string;
  role: string;
  content: string;
  name?: string;
  is_good_response?: boolean;
  isEditing?: boolean;
  originalContent?: string;
  internal: boolean;
}

@Component({
  selector: 'chat',
  imports: [
    FormsModule,
    CommonModule,
    MarkdownComponent,
    NgIconComponent,
    ClarityModule,
    MenuModule,
    TextareaComponent
  ],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.css',
  providers: [
    provideMarkdown({
      sanitize: SecurityContext.NONE,
    }),
    provideIcons({
      heroHandThumbUp,
      heroHandThumbDown,
      heroHandThumbDownSolid,
      heroHandThumbUpSolid,
      heroPencilSquareSolid,
      heroUserSolid,
    }),
  ],
})
export class ChatComponent implements AfterViewInit, OnInit {
  @ViewChild('actionItems') actionItems: ElementRef | undefined;
  @ViewChild(TextareaComponent) textareaComponent!: TextareaComponent;

  private labelRequested = false;
  constructor(
    private auth: OauthService,
    public aiService: AiService,
    private userSettings: UserSettingsService,
    private repoService: RepoService,
    private workflow: WorkflowService,
    private http: HttpClient,
    private db: DbService,
  ) {
    effect(async () => {
      if (
        this.history().length === 3 &&
        this.aiService.session_label === null &&
        !this.labelRequested
      ) {
        this.labelRequested = true;
        try {
          const res = await lastValueFrom(
            this.http.post<string>(
              `${environment.backendUrl}/api/chat/label`, this.history().slice(0, 2)
            ));
          this.aiService.session_label = res;
        } catch (error) {
          console.error('Error getting chat label:', error);
        }
      }
    });

    this.workflow.currentWorkFlow$.subscribe(wf => {
      this.selectedWorkflow = wf;
    });
  }

  @Input() sessionId!: string;
  @Input() shell!: ShellComponent;

  createRepoVisible: boolean = false;
  msgInput: string = '';
  selectedWorkflow: WorkflowType | null = null;

  // history: Message[] = [];
  history = signal<Message[]>([]);
  uiHistory = computed(() => {
    const pubMessages = this.history().filter(msg => !msg.internal);
    if (pubMessages.length === 0) return [];
    const merged: Message[] = [];

    for (const msg of pubMessages) {
      if (
        merged.length > 0 &&
        msg.role === 'assistant' &&
        merged[merged.length - 1].role === 'assistant'
      ) {
        // Only add separator if the previous content is not empty
        merged[merged.length - 1] = {
          ...merged[merged.length - 1],
          content: merged[merged.length - 1].content
            ? merged[merged.length - 1].content + "\n\n" + msg.content
            : msg.content,
          message_id: merged[merged.length - 1].message_id + '_' + msg.message_id,
        };
      } else {
        merged.push({ ...msg });
      }
    }
    return merged;
  });


  aiWriteSection: string | null = 'message';
  assistantInternalMsgBuffer: string = '';

  // Add new property to store messages div reference
  private messagesDiv: HTMLElement | null = null;

  // Add message animation properties
  newMessageAnimation: boolean = false;

  // Add feedback animation properties
  feedbackAnimating: boolean = false;

  // Add scroll behavior properties
  smoothScrollEnabled: boolean = true;

  failureCount: number = 0;

  // isActionItemsOpen = false;

  user: UserInfo | null = null;

  ngOnInit() {
    this.auth.getUser().then((user) => {
      if (user) {
        this.user = user;
      }
    });

    this.aiService.aiWritingChunk$.subscribe(async (chunk) => {
      if (chunk.section === 'mcp' && chunk.fullContent) {
        // Handle MCP chunks
        let parsedArgs: object = {};
        let tool: string = '';
        // console.log('mcpChunk', chunk.fullContent);
        try {
          parsedArgs = chunk.fullContent && typeof chunk.fullContent === 'string' ? JSON.parse(chunk.fullContent) : chunk.fullContent;
        } catch (e) {
          parsedArgs = {};
          throw new Error('Error parsing MCP chunk content', { cause: { code: "MCP_PARSE_ERROR", error: e } });
        }
        tool = chunk.uri!;
        // console.log('mcpTool', tool);

        this.history.update(history => [
          ...history,
          {
            session_id: this.sessionId,
            message_id: crypto.randomUUID(),
            role: 'assistant',
            content: `<div
                        class="collapse collapse-arrow bg-gray-200/50 border border-gray-300/50
                          w-full flex-shrink-0 rounded-lg shadow-lg hover:border-gray-600/50 transition-all duration-200 my-5"
                        >
                        <div
                        class="collapse-title p-3 flex gap-1 cursor-pointer after:top-[50%]! rounded-lg hover:bg-gray-300/30 transition-colors"
                        onclick="this.parentElement.classList.toggle('collapse-open');
                          this.parentElement.classList.toggle('collapse-close');
                          this.parentElement.querySelector('.collapse-content').classList.toggle('hidden');"
                        >
                          <span class="flex items-center gap-2 text-blue-400 font-semibold">
                            Tool Call : 
                          </span>
                          <div class="text-sm text-gray-800 font-medium capitalize mt-1 ml-0.5">${tool}</div>
                        </div>
                        <div class="collapse-content text-sm p-3 hidden bg-gray-300/30 rounded-b-lg border-t border-gray-300/50">
                          ${Object.entries(parsedArgs).map(([key, value]) => this.formatToolCall(key, value)).join('')}
                        </div>
                      </div>`,
            internal: false,
          }
        ]);
      } else if (chunk.section && ['message', 'question', 'answer'].includes(chunk.section)) {
        // Handle message chunks and color final answer text when plain text arrives without a dedicated event.
        try {
          this.history.update(history => {
            const newHistory = [...history];
            for (let i = newHistory.length - 1; i >= 0; i--) {
              if (newHistory[i].role === 'assistant' && !newHistory[i].internal) {
                newHistory[i] = {
                  ...newHistory[i],
                  content: newHistory[i].content + chunk.chunk
                };
                break;
              }
            }
            return newHistory;
          });
          setTimeout(() => this.scrollToBottom(), 0);
        } catch (error) {
          console.error('Error updating message content:', error);
        }
      }
    });

    this.aiService.aiWriteSection$.subscribe((section) => {
      this.aiWriteSection = section;
      let append = "";
      if (section === 'message') {
        this.history.update(history => [
          ...history,
          {
            session_id: this.sessionId,
            message_id: crypto.randomUUID(),
            role: 'assistant',
            content: '',
            internal: false,
          }
        ]);
      } else if (section === 'answer' || section === 'question') {
        // A block <div> with blank lines around the content, not an inline
        // <span>: markdown splits the text into paragraphs at every blank line,
        // so a <span> got auto-closed after the first paragraph and the closing
        // </span> matched nothing. Markdown inside a block div still renders.
        append = `<div style='color:${section === 'answer' ? '#6edd6e' : '#52b8fa'};' class="font-semibold tracking-wide">\n\n`;
      } else if (section === 'close:answer' || section === 'close:question') {
        append = "\n\n</div>";
      } else {
        return;
      }
      this.history.update(history => {
        const newHistory = [...history];
        // Find the last assistant message
        for (let i = newHistory.length - 1; i >= 0; i--) {
          if (newHistory[i].role === 'assistant' && !newHistory[i].internal) {
            newHistory[i] = {
              ...newHistory[i],
              content: newHistory[i].content + '\n' + append
            };
            break;
          }
        }
        return newHistory;
      });
    });

    this.aiService.aiIsWriting$.subscribe(async isWriting => {
      if (!isWriting) {
        this.scrollToBottom();
        if (this.history().length > 3) {
          const repoId = this.repoService.repo()?.id;
          if (!repoId || !/^\d+$/.test(repoId)) return;
          try {
            await this.db.upsertUiHistory({
              session_id: this.sessionId,
              history: this.history(),
              repo_id: repoId,
              label: this.aiService.session_label,
            });
          } catch (error) {
            console.error('Error upserting history:', error);
          }
        }
      }
    });

    this.aiService.workflowState$.subscribe((workflowUpdate) => {
      if (!workflowUpdate) return;
      const messageColor = workflowUpdate.type == 'success' ? 'success' : workflowUpdate.type == 'info' ? 'info' : 'error';
      this.history.update(history => [
        ...history,
        {
          session_id: this.sessionId,
          message_id: crypto.randomUUID(),
          role: 'assistant',
          content: `<p class="text-${messageColor} my-3">${workflowUpdate.message}</p>`,
          internal: false,
        }
      ]);
    });

    this.repoService.sessionChanged$.subscribe(async (sessionId) => {
      if (!sessionId) {
        this.history.set([]);
        return;
      }
      let data;
      try {
        data = await this.db.getUiHistory(sessionId);
      } catch (error) {
        throw new Error('Error fetching session history:', { cause: { code: "HISTORY_FETCH_ERROR", error } });
      }
      const messages = data.history;
      if (messages) {
        for (const message of messages) {
          const regex = /<(createFile|mcp|viewFile|runShell|system_response|tool_code|xml)([^>]*?)(?:\/>|>[\s\S]*?<\/\1>)/gm;
          message.content = message.content.replace(regex, "");
        }
      }

      const newHistory = messages.map((msg: Message) => ({
        session_id: msg.session_id,
        message_id: crypto.randomUUID(),
        role: msg.role,
        content: msg.content,
        is_good_response: null,
        isEditing: false,
        originalContent: msg.content,
        internal: msg.internal,
      })) as Message[];

      this.history.set(newHistory);
      this.scrollToBottom();
    });
  }

  ngAfterViewInit() {
    this.messagesDiv = document.getElementById('messages');

    // Add auto-resize functionality to textarea
    const textarea = document.querySelector('textarea');
    if (textarea) {
      textarea.addEventListener('input', function () {
        this.style.height = 'auto';
        this.style.height = this.scrollHeight + 'px';
      });
    }
  }

  // Enhance scroll helper method with smooth scrolling
  private scrollToBottom() {
    if (this.messagesDiv) {
      this.messagesDiv.scrollTo({
        top: this.messagesDiv.scrollHeight,
        behavior: 'smooth',
      });
    }
  }

  handleFeedback(msg: Message, isGood: boolean) {
    // Add animation when feedback is given
    this.feedbackAnimating = true;
    setTimeout(() => {
      this.feedbackAnimating = false;
    }, 1000);

    msg.is_good_response = isGood;
  }

  async handleSend(msgInput: string): Promise<void> {
    const messageContent = msgInput;
    this.msgInput = ''; // Clear the input immediately

    this.history.update(history => [
      ...history,
      {
        session_id: this.sessionId,
        message_id: crypto.randomUUID(),
        role: 'user',
        content: messageContent,
        internal: false,
      },
      {
        session_id: this.sessionId,
        message_id: crypto.randomUUID(),
        role: 'assistant',
        content: '',
        internal: false,
      }
    ]);

    // const details = await this.gatherDetails();
    if (!this.selectedWorkflow) {
      await this.aiService.handleAsk(this.history()[this.history().length - 2]);
    } else {
      await this.aiService.handleWorkflow(this.history()[this.history().length - 2], this.selectedWorkflow);
    }
  }

  handleStop() {
    this.aiService.setAiIsWriting(false);
  }

  // Add method to check if message is the last one
  isLastMessage(index: number): boolean {
    return index === this.history().length - 1;
  }

  // Add method to check if message is from AI and currently being written
  isActiveAiMessage(msg: Message, index: number): boolean {
    return (
      msg.role === 'assistant' &&
      this.aiService.getAiIsWritingValue() &&
      this.isLastMessage(index)
    );
  }

  editMessage(message: Message) {
    message.isEditing = true;
    message.originalContent = message.content;
  }

  async saveEditMessage(message: Message) {
    message.isEditing = false;
    // const editedMessageId = message.message_id;

    // remove rest of the messages
    const index = this.history().findIndex((msg: Message) => msg.message_id === message.message_id);
    if (message.role === 'user' && index < this.history().length - 1) {
      // Update the user message content
      this.history.update(history => {
        const newHistory = [...history];
        newHistory[index] = { ...newHistory[index], content: message.content };
        return newHistory;
      });
    }
    this.history.update(history => history.slice(0, index + 1));

    // start a new conversation from this point onwards
    // const details = await this.gatherDetails();
    this.aiService.handleAsk(this.history()[this.history().length - 1]);
  }

  closeEditMessage(message: Message) {
    message.isEditing = false;
    message.content = message.originalContent || '';
  }

  getId(obj: Message): string {
    return obj.message_id;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatToolCall(key: string, value: any): string {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return `<div class="flex flex-col gap-1 py-1 text-gray-800">
        <span class="text-gray-800 font-medium">${key}:</span>
        <div class="ml-4 pl-2">
          ${Object.entries(value)
          .map(([subKey, subValue]) => this.formatToolCall(subKey, subValue))
          .join('')}
        </div>
      </div>`;
    } else {
      return `<div class="flex items-center gap-2 py-1 text-gray-800">
      <span class="text-gray-800 font-medium">${key}:</span>
      <span class="text-black break-all whitespace-pre-line max-w-full">${value}</span>
    </div>`;
    }
  }

  updateWorkflow(workflow: WorkflowType) {
    this.workflow.setWorkflow(workflow);
  }
}
